import { useState } from 'react';
import {
  Paper,
  Stack,
  Group,
  Button,
  Text,
  Modal,
  TextInput,
  ActionIcon,
  Card,
  Title,
  Alert,
  Center,
  Loader,
  Switch,
  Badge,
} from '@mantine/core';
import { IconPlus, IconTrash, IconEdit, IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import {
  useStylesheets,
  useCreateStylesheet,
  useUpdateStylesheet,
  useDeleteStylesheet,
  PdfStylesheet,
} from '../../api/stylesheets';
import { CodeMirrorCssEditor } from '../shared/CodeMirrorCssEditor';

const DEFAULT_CSS = `/* PDF Stylesheet - Customize styles for your resume PDF export */

@page {
  size: 8.5in 11in;
  margin: 0.5in;
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 11pt;
  line-height: 1.5;
  color: #333;
  margin: 0;
  padding: 0;
}

h1, h2, h3, h4, h5, h6 {
  margin-top: 0.5em;
  margin-bottom: 0.25em;
  color: #1a1a1a;
  font-weight: 600;
}

h1 { font-size: 18pt; }
h2 { font-size: 14pt; border-bottom: 1px solid #ccc; padding-bottom: 0.1em; }
h3 { font-size: 12pt; }

p {
  margin: 0 0 0.5em 0;
}

ul, ol {
  margin: 0.5em 0;
  padding-left: 1.5em;
}

li {
  margin-bottom: 0.25em;
}

code {
  background-color: #f5f5f5;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

pre {
  background-color: #f5f5f5;
  padding: 1em;
  border-radius: 5px;
  overflow-x: auto;
  margin: 0.5em 0;
}

blockquote {
  margin-left: 1.5em;
  padding-left: 0.5em;
  border-left: 3px solid #ddd;
  color: #666;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5em 0;
  font-size: 10pt;
}

th, td {
  border: 1px solid #ddd;
  padding: 0.5em;
  text-align: left;
}

th {
  background-color: #f5f5f5;
  font-weight: 600;
}

a {
  color: #0066cc;
  text-decoration: underline;
}

strong {
  font-weight: 600;
}

em {
  font-style: italic;
}

hr {
  border: none;
  border-top: 1px solid #ddd;
  margin: 1em 0;
}`;

export function StylesheetManager() {
  const { data: stylesheets = [], isLoading } = useStylesheets();
  const createMutation = useCreateStylesheet();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditingId, setCurrentEditingId] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Use the editing ID for mutations
  const updateMutation = useUpdateStylesheet(currentEditingId || '');
  const deleteMutation = useDeleteStylesheet(currentEditingId || '');

  const [formData, setFormData] = useState<Omit<PdfStylesheet, 'id' | 'created_at' | 'updated_at'>>({
    name: '',
    description: '',
    css_content: DEFAULT_CSS,
    is_default: false,
  });

  const handleOpenModal = (stylesheet?: PdfStylesheet) => {
    if (stylesheet) {
      setCurrentEditingId(stylesheet.id);
      setFormData({
        name: stylesheet.name,
        description: stylesheet.description || '',
        css_content: stylesheet.css_content,
        is_default: stylesheet.is_default,
      });
    } else {
      setCurrentEditingId(null);
      setFormData({
        name: '',
        description: '',
        css_content: DEFAULT_CSS,
        is_default: false,
      });
    }
    setResultMessage(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentEditingId(null);
    setResultMessage(null);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.css_content) {
      setResultMessage({
        type: 'error',
        message: 'Name and CSS content are required',
      });
      return;
    }

    try {
      if (currentEditingId) {
        await updateMutation.mutateAsync(formData);
        setResultMessage({
          type: 'success',
          message: 'Stylesheet updated successfully',
        });
      } else {
        await createMutation.mutateAsync(formData);
        setResultMessage({
          type: 'success',
          message: 'Stylesheet created successfully',
        });
      }
      setTimeout(handleCloseModal, 1500);
    } catch (error) {
      setResultMessage({
        type: 'error',
        message: error instanceof Error ? error.message : 'Operation failed',
      });
    }
  };

  const handleDelete = async (id: string, isDefault: boolean) => {
    if (isDefault) {
      setResultMessage({
        type: 'error',
        message: 'Cannot delete the default stylesheet. Set another stylesheet as default first.',
      });
      return;
    }

    if (confirm('Are you sure you want to delete this stylesheet?')) {
      setCurrentEditingId(id);
      try {
        await deleteMutation.mutateAsync();
        setResultMessage({
          type: 'success',
          message: 'Stylesheet deleted successfully',
        });
      } catch (error) {
        setResultMessage({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to delete stylesheet',
        });
      }
    }
  };

  return (
    <Paper p="lg" radius="md" withBorder>
      <Stack gap="md">
        <div>
          <Group justify="space-between" align="center">
            <div>
              <Title order={2} size="h3">
                Stylesheets
              </Title>
              <Text size="sm" c="dimmed">
                Create and manage custom CSS stylesheets for resume rendering and PDF exports
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={() => handleOpenModal()}>
              Create Stylesheet
            </Button>
          </Group>
        </div>

        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : stylesheets.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            No custom stylesheets created yet. Create one to customize your resume rendering and PDF exports.
          </Alert>
        ) : (
          <Stack gap="sm">
            {stylesheets.map((stylesheet) => (
              <Card key={stylesheet.id} padding="sm" radius="md" withBorder>
                <Card.Section p="sm" inheritPadding>
                  <Group justify="space-between" align="flex-start">
                    <div style={{ flex: 1 }}>
                      <Group gap="xs" mb="xs">
                        <div>
                          <Text fw={500} size="sm">
                            {stylesheet.name}
                          </Text>
                          {stylesheet.description && (
                            <Text size="xs" c="dimmed">
                              {stylesheet.description}
                            </Text>
                          )}
                        </div>
                        {stylesheet.is_default && <Badge color="blue" size="sm">Default</Badge>}
                      </Group>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        CSS stylesheet for resume rendering and PDF export
                      </Text>
                    </div>

                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => handleOpenModal(stylesheet)}
                      >
                        Edit
                      </Button>
                      <ActionIcon
                        color="red"
                        size="sm"
                        variant="light"
                        onClick={() => handleDelete(stylesheet.id, stylesheet.is_default)}
                        disabled={stylesheet.is_default}
                        title={stylesheet.is_default ? 'Cannot delete the default stylesheet' : 'Delete stylesheet'}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Group>
                </Card.Section>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      {/* Stylesheet Editor Modal */}
      <Modal
        opened={isModalOpen}
        onClose={handleCloseModal}
        title={currentEditingId ? 'Edit Stylesheet' : 'Create New Stylesheet'}
        size="xl"
        styles={{
          body: { display: 'flex', flexDirection: 'column', height: '70vh', padding: 'var(--mantine-spacing-md)' },
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 'var(--mantine-spacing-md)' }}>
          {/* Scrollable Content Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mantine-spacing-md)', overflowY: 'auto', flex: 1 }}>
            {resultMessage && (
              <Alert
                icon={resultMessage.type === 'success' ? <IconCheck size={16} /> : <IconX size={16} />}
                color={resultMessage.type === 'success' ? 'green' : 'red'}
              >
                {resultMessage.message}
              </Alert>
            )}

            {/* Name */}
            <TextInput
              label="Stylesheet Name"
              placeholder="e.g., Professional Blue"
              value={formData.name}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  name: e.currentTarget.value,
                })
              }
            />

            {/* Description */}
            <TextInput
              label="Description (Optional)"
              placeholder="What is this stylesheet for?"
              value={formData.description}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  description: e.currentTarget.value,
                })
              }
            />

            {/* Default Toggle */}
            <Switch
              label="Set as default stylesheet for resume rendering and PDF exports"
              checked={formData.is_default}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  is_default: e.currentTarget.checked,
                })
              }
            />            

            {/* CSS Content */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                CSS Content
              </Text>
              <CodeMirrorCssEditor
                value={formData.css_content}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    css_content: value,
                  })
                }
                minHeight="400px"
              />
            </div>


          </div>

          {/* Fixed Action Buttons */}
          <Group justify="flex-end" style={{ flex: 0, flexShrink: 0, paddingTop: 'var(--mantine-spacing-md)' }}>
            <Button variant="default" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {currentEditingId ? 'Update Stylesheet' : 'Create Stylesheet'}
            </Button>
          </Group>
        </div>
      </Modal>
    </Paper>
  );
}
